/* eslint-disable import/prefer-default-export */

import dayjs from 'dayjs'
import { cache } from 'swr'

import { store } from '../store'
import { mutate } from '../hooks/useSWR'

const properties = {
  lock: {
    collectionName: 'locks',
    fields: {
      custom_name: {
        type: 'string',
        translation: 'Namn',
      },
      location: {
        type: 'string',
        translation: 'Platsbeskrivning',
      },
      port_id: {
        type: 'number',
        translation: 'Port-ID',
      },
      street: {
        type: 'string',
        translation: 'Gata',
      },
      city: {
        type: 'string',
        translation: 'Stad',
      },
      zip: {
        type: 'string',
        translation: 'Postnummer',
      },
      doors: {
        type: 'array',
        translation: 'Dörrar',
        fields: {
          id: {
            type: 'string',
            translation: 'id',
          },
          port_id: {
            type: 'string',
            translation: 'Port',
          },
          display_name: {
            type: 'string',
            translation: 'Visningsnamn',
          },
          allocation: {
            type: 'object',
            translation: 'Allokering',
            fields: {
              easy_access: { type: 'ref', translation: 'Gästnyckel' },
              delivery: { type: 'ref', entity: 'delivery', translation: 'Leverans' },
              retur: { type: 'ref', entity: 'retur', translation: 'Retur' },
            },
          },
          controller_index: {
            type: 'string',
            translation: 'controller_index',
          },
          dimensions: {
            type: 'array',
            translation: 'Storlek',
          },
        },
      },
    },
  },
  controller: {
    collectionName: 'controllers',
    fields: {
      open_duration: {
        type: 'number',
        translation: 'Öppningstid (millisekunder)',
      },
      accessid: {
        type: 'string',
        translation: 'Qlocx ID',
      },
    },
  },
  admin: {
    collectionName: 'admins',
    fields: {
      name: {
        type: 'string',
        translation: 'Namn',
      },
    },
  },
  driver: {
    collectionName: 'drivers',
    fields: {
      name: {
        type: 'string',
        translation: 'Namn',
      },
      surname: {
        type: 'string',
        translation: 'Efternamn',
      },
      email: {
        type: 'string',
        translation: 'Email',
      },
    },
  },
}

const translations = {
  easy_access: 'Gästnyckel',
  delivery: 'Leverans',
  retur: 'Retur',
  created: 'Skapad',
  unlimited_uses: 'Obegränsat med användningar',
  target: 'Mottagare',
  false: 'Nej',
  true: 'Ja',
  name: 'Namn',
  surname: 'Efternamn',
  phone: 'Mobiltelefonnummer',
  password: 'Lösenord',
  email: 'Email',
  contractor: 'Åkeri',
  area: 'Område',
  driver_id: 'Chaufförs-id',
  terminal: 'Åkeri',
  k2username: 'Inlogg K2',
  projectNumber: 'Projektnummer',
  hilti: 'Hilti',
  ahlsell: 'Ahlsell',
  contactPerson: 'Administratör',
  contactPersonName: 'Namn',
  contactPersonPhone: 'Telefonnummer',
  contactPersonEmail: 'Email',
  storageItems: 'Produkter',
  storageOrders: 'Uttagshistorik',
  storageUsers: 'Användare',
  storageSettings: 'Förrådsinställningar',
  READ: 'Se inventarier',
  CREATEORDER: 'Hämta ut varor',
  MODIFYINVENTORY: 'Fylla på förrådet',
  defaultMarking: 'Standardmärkning på uttag om inte annat anges',
  storageSupplierOrders: 'Leverantörsordrar',
  vendorName: 'Avsändarnamn',
  street: 'Gatuadress',
  carrierInternalOrderId: 'Internt orderId',
  driverPhone: 'Chaufförs tel.',
  custom_name: 'Projektets namn',
  location: 'Beskrivning av dörrens placering',
  owner: 'Administratörens telefonnummer',
}

export const getProperty = (entity, key) => {
  if (!key) return ''
  const str = properties[entity].fields[key] || key
  return str
}

export const translate = (str) => translations[str] || str

export const getCollectionName = (entity) => properties[entity].collectionName

export const chunkArray = (arr, size) => {
  if (!arr) return []
  const myArray = []

  for (let i = 0; i < arr.length; i += size) {
    myArray.push(arr.slice(i, i + size))
  }

  return myArray
}

export const deliveryIsFinished = (delivery) => delivery && delivery.checkoutDate && delivery.checkoutDate.length

export const stringMatchInsensitive = (value, str) => {
  if (!value || !str) return false

  if (typeof value !== 'string' || typeof str !== 'string') return false

  const regex = new RegExp(str, 'i')

  return regex.test(value)
}

const saves = {}
export const queueSave = (id, item, ms) =>
  new Promise((resolve, reject) => {
    if (!saves[id]) {
      saves[id] = { items: [item] }
    } else {
      saves[id].items.push(item)
      clearTimeout(saves[id].timeoutID)
    }

    saves[id].timeoutID = setTimeout(() => {
      resolve(saves[id].items)
      delete saves[id]
    }, ms)
  })

export const isObjectEmpty = (obj) => Object.entries(obj).length === 0 && obj.constructor === Object

export const getCompany = (companyId) => {
  const state = store.getState()

  try {
    return state.collectionData.companies.keyedCompanies[companyId]
  } catch (e) {
    return null
  }
}

export const getUserCompanyOnLock = (lockId, customerId) => {
  const state = store.getState()

  const lock = state.collectionData.locks.find((l) => l._id === lockId)
  if (!lock) return null

  const userAccess = lock.users.find((u) => u.customer === customerId)
  if (!userAccess) return null
  if (!userAccess.company) return null

  try {
    return state.collectionData.companies.keyedCompanies[userAccess.company]
  } catch (e) {
    return null
  }
}

export const populateCompaniesOnLock = (lockId) => {
  const state = store.getState()

  const lock = state.collectionData.locks.find((l) => l._id === lockId)
  if (!lock) return null

  lock.users = lock.users.map((user) => {
    if (!user.company) return user

    user.company = state.collectionData.companies.keyedCompanies[user.company]

    return user
  })

  return lock
}

export const keyify = (array, key) => {
  if (!array) return {}

  const reduced = array.reduce((acc, val) => {
    acc[val[key]] = val
    return acc
  }, {})

  return reduced
}

export const getModalStyles = (scale = 1) => {
  const im = window.isMobileF()

  const styles = {
    content: {
      top: im ? '0' : `${scale * 15}vh`,
      left: im ? '0' : `${scale * 25}vh`,
      bottom: im ? '0' : `${scale * 15}vh`,
      right: im ? '0' : `${scale * 25}vh`,
      overflowX: 'hidden',
    },
  }

  return styles
}

export const getSubModalStyles = (scale = 1) => {
  const im = window.isMobileF()

  const styles = {
    content: {
      top: im ? '0' : `${scale * 15}vh`,
      left: im ? '0' : `${scale * 35}vh`,
      bottom: im ? '0' : `${scale * 15}vh`,
      right: im ? '0' : `${scale * 35}vh`,
    },
  }

  return styles
}

export const byField = (field) => (a, b) => (field ? (a[field] > b[field] ? 1 : -1) : a > b ? 1 : -1)

export function groupBy(list, keyGetter) {
  const map = new Map()
  list.forEach((item) => {
    const key = keyGetter(item)
    const collection = map.get(key)
    if (!collection) {
      map.set(key, [item])
    } else {
      collection.push(item)
    }
  })
  return map
}

export const deliveredToContainer = (delivery) => delivery.qsign !== false && delivery.isObservableMatch !== true

export const niceDate = (isoString) => dayjs(isoString).format('D/M HH:mm')

export const niceDateWithYear = (isoString) => dayjs(isoString).format('D/M/YY HH:mm')

/* update all cache where item exists */
export const updateSwrCache = async (key, func, shouldRevalidate) =>
  Promise.all(
    cache
      .keys()
      .filter((k) => k.startsWith(key))
      .map((k) => mutate(k, func, shouldRevalidate))
  )

export const dateFromObjectId = (objectId) => new Date(parseInt(objectId.substring(0, 8), 16) * 1000)

export const sendToIframe = (msg, iframeTitle) => {
  const iframe = document.querySelector(`iframe[title="${iframeTitle}"]`)

  iframe?.contentWindow?.postMessage(JSON.stringify(msg), '*')
}

window.sendToIframe = sendToIframe

export default sendToIframe
